import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import RootCarousel, {
    CarouselRenderItem,
    ICarouselInstance,
    TCarouselProps,
} from "react-native-reanimated-carousel";
import Dots from "./Dots";
import { SharedValue, useSharedValue } from "react-native-reanimated";
import { useTheme } from "../hooks/use-theme";
import { TParallaxModeProps } from "react-native-reanimated-carousel/src/layouts/parallax";
import { TStackModeProps } from "react-native-reanimated-carousel/src/layouts/stack";
import { XOR } from "ts-essentials";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import VStack from "../ui_atoms/VStack";
import CarouselElement, { CarouselElementType } from "./CarouselElement";

type WelcomeCarouselIndex = number | "full";

export type CarouselProps = {
    children: React.ReactNode[];
    dotColor?: string;
    dotsOffset?: number;
    maxItemWidth?: number;
    gestureHandlerStyle?: StyleProp<ViewStyle>;
    scrollValue?: SharedValue<number>;
    elements?: CarouselElementType[];
    canPrev?: (canPrev: boolean) => void;
    canNext?: (canNext: boolean) => void;
    onCarouselChange?: (toIndex: number) => void;
} & Omit<TCarouselProps, "data" | "renderItem" | "width"> &
    XOR<Omit<TParallaxModeProps, "width" | "vertical">, TStackModeProps>;

export type CarouselRef = {
    prev: () => WelcomeCarouselIndex;
    next: () => WelcomeCarouselIndex;
    getCurrentIndex: () => WelcomeCarouselIndex;
};

const Carousel = ({
    children,
    dotColor,
    dotsOffset,
    maxItemWidth,
    style,
    height,
    gestureHandlerStyle,
    elements,
    canPrev,
    canNext,
    onCarouselChange,
    ref: carouselRef,
    ...props
}: CarouselProps & { ref?: React.Ref<CarouselRef> }) => {
    const styles = useStyles();
    const scrollValue = useSharedValue(0);
    const actualIndex = useRef(0);
    const isScrolling = useRef(false);
    const isLarge = useIsScreenSizeMin("large");

    const [viewWidth, setViewWidth] = useState(0);
    const ref = useRef<ICarouselInstance>(null);

    const renderItem = useCallback<CarouselRenderItem<React.ReactNode>>(({ item }) => {
        return <>{item}</>;
    }, []);

    const goToIndex = useCallback(
        (index: number) => {
            onCarouselChange?.(index);
            isScrolling.current = true;
            actualIndex.current = index;
            ref.current?.scrollTo({
                index,
                animated: true,
                onFinished: () => {
                    isScrolling.current = false;
                },
            });
            canPrev?.(!(actualIndex.current === 0));
            canNext?.(!(actualIndex.current >= children.length - 1));
        },
        [canNext, canPrev, children.length, onCarouselChange],
    );

    useImperativeHandle(carouselRef, () => ({
        prev: () => {
            goToIndex(actualIndex.current - 1);
            return actualIndex.current - 1;
        },
        next: () => {
            if (actualIndex.current < children.length - 1) {
                goToIndex(actualIndex.current + 1);
                return actualIndex.current + 1;
            } else {
                goToIndex(0);
                return 0;
            }
        },
        getCurrentIndex: () => actualIndex.current,
    }));

    const onProgressChange = useCallback(
        (_: number, absoluteProgress: number) => {
            scrollValue.set(absoluteProgress);
            const newIndex = Math.round(absoluteProgress);
            if (newIndex !== actualIndex.current && !isScrolling.current) {
                actualIndex.current = newIndex;
                onCarouselChange?.(newIndex);
            }
        },
        [scrollValue, onCarouselChange],
    );

    return (
        <VStack
            gap={1}
            onLayout={(e) => {
                setViewWidth(e.nativeEvent.layout.width);
            }}
            style={[
                {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flex: 1,
                },
                style,
            ]}
        >
            {viewWidth ? (
                <RootCarousel
                    {...props}
                    ref={ref}
                    style={styles.carousel}
                    onProgressChange={onProgressChange}
                    width={!isLarge ? viewWidth : maxItemWidth || viewWidth}
                    height={height || 0}
                    data={children}
                    renderItem={renderItem}
                    containerStyle={[{ height: "100%" }, gestureHandlerStyle]}
                />
            ) : null}
            {elements?.map((element, index) => (
                <CarouselElement
                    key={index}
                    scrollValue={scrollValue}
                    element={element}
                    totalLength={children.length}
                />
            ))}
            <View
                style={[
                    styles.dotsContainer,
                    dotsOffset !== undefined && {
                        position: "absolute",
                        marginVertical: 0,
                        bottom: dotsOffset,
                    },
                ]}
            >
                <Dots
                    totalDots={children.length}
                    currentDot={scrollValue}
                    color={dotColor}
                    onPress={goToIndex}
                />
            </View>
        </VStack>
    );
};

const useStyles = () => {
    const { colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                carousel: {
                    overflow: "visible",
                },
                dotsContainer: {
                    paddingVertical: 6,
                    paddingHorizontal: 8,
                    borderRadius: 100,
                    backgroundColor: colors.background,
                },
            }),
        [colors.background],
    );
};

export default Carousel;
